import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage, languageNames } from "../contexts/LanguageContext";
import { toast } from "sonner";
import DownloadAppButton, { FloatingInstallBanner } from "../components/DownloadAppButton";
import StripePayment from "../components/StripePayment";
import { trackPageView } from "../utils/analytics";
import { 
  Scan, 
  Radar, 
  Aperture, 
  Eye, 
  Globe, 
  Crown,
  ArrowRight,
  Sparkles,
  ChevronDown,
  Radio,
  Wifi,
  Palette,
  Lock,
  Shield
} from "lucide-react";

// PUBLIC THEMES - Available to all users (random selection)
const publicThemes = {
  crystal: {
    name: "Crystal Clear",
    nameTranslations: {
      en: "Crystal Clear",
      es: "Cristal Claro",
      fr: "Cristal Clair",
      de: "Kristallklar",
      zh: "水晶透明",
      ja: "クリスタルクリア",
      pt: "Cristal Claro",
      ar: "كريستال صافي",
      ru: "Кристально Чистый",
      ko: "크리스탈 클리어"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/666192851a44bf15c91ae035bb01a02589f04647803940daaebf38089fc4305a.png",
    overlay: "rgba(0,0,0,0.1)",
    blur: "blur(0px)",
    accent: "#00ff88",
    glow: "rgba(0,255,136,0.5)",
    gradient: "radial-gradient(ellipse at center, rgba(0,10,20,0.3) 0%, rgba(0,5,10,0.7) 70%)"
  },
  frosted: {
    name: "Frosted Glass",
    nameTranslations: {
      en: "Frosted Glass",
      es: "Vidrio Esmerilado",
      fr: "Verre Givré",
      de: "Milchglas",
      zh: "磨砂玻璃",
      ja: "フロストガラス",
      pt: "Vidro Fosco",
      ar: "زجاج مصنفر",
      ru: "Матовое Стекло",
      ko: "프로스티드 글라스"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/e06038760ec1316478ad2e95869e083ea6cf2f525da083a20d69b3ec35eb4eef.png",
    overlay: "rgba(0,20,30,0.15)",
    blur: "blur(4px)",
    accent: "#00ffcc",
    glow: "rgba(0,255,204,0.3)",
    gradient: "radial-gradient(ellipse at center, rgba(0,40,50,0.4) 0%, rgba(0,10,15,0.7) 70%)"
  },
  holographic: {
    name: "Holographic",
    nameTranslations: {
      en: "Holographic",
      es: "Holográfico",
      fr: "Holographique",
      de: "Holografisch",
      zh: "全息",
      ja: "ホログラフィック",
      pt: "Holográfico",
      ar: "هولوغرافي",
      ru: "Голографический",
      ko: "홀로그래픽"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/653c5841a6aaf0a36c55a0bc51a1bb25dc1f086597c58557a31c5b175336ea18.png",
    overlay: "rgba(10,0,20,0.1)",
    blur: "blur(0px)",
    accent: "#ff66ff",
    glow: "rgba(255,102,255,0.3)",
    gradient: "radial-gradient(ellipse at center, rgba(30,10,40,0.35) 0%, rgba(10,5,15,0.7) 70%)"
  }
};

// OWNER-ONLY THEMES - Creepy/Scary themes only for owner mode
const ownerThemes = {
  abduction: {
    name: "Abduction",
    nameTranslations: {
      en: "Abduction",
      es: "Abducción",
      fr: "Enlèvement",
      de: "Entführung",
      zh: "绑架",
      ja: "アブダクション",
      pt: "Abdução",
      ar: "اختطاف",
      ru: "Похищение",
      ko: "납치"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/00c9833b5af475885fe3668af48ebcb9a25a3e8a88dc9a115445d23b4e308b2d.png",
    overlay: "rgba(0,0,0,0.05)",
    blur: "blur(0px)",
    accent: "#ff3333",
    glow: "rgba(255,50,50,0.4)",
    gradient: "radial-gradient(ellipse at center, rgba(30,5,5,0.3) 0%, rgba(10,0,0,0.7) 70%)"
  },
  invasion: {
    name: "Invasion",
    nameTranslations: {
      en: "Invasion",
      es: "Invasión",
      fr: "Invasion",
      de: "Invasion",
      zh: "入侵",
      ja: "侵略",
      pt: "Invasão",
      ar: "غزو",
      ru: "Вторжение",
      ko: "침공"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/bb0125e644ef9e99dc4a75da0d07e5107e036579dc7e236d3e3ec49c36792fb0.png",
    overlay: "rgba(0,0,0,0.05)",
    blur: "blur(0px)",
    accent: "#66ff66",
    glow: "rgba(100,255,100,0.4)",
    gradient: "radial-gradient(ellipse at center, rgba(10,15,10,0.3) 0%, rgba(5,10,5,0.7) 70%)"
  },
  encounter: {
    name: "Close Encounter",
    nameTranslations: {
      en: "Close Encounter",
      es: "Encuentro Cercano",
      fr: "Rencontre Rapprochée",
      de: "Nahbegegnung",
      zh: "近距离接触",
      ja: "接近遭遇",
      pt: "Encontro Imediato",
      ar: "لقاء قريب",
      ru: "Близкий Контакт",
      ko: "근접 조우"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/9dad50aa52c20ff404abed9b0b92298359f51e5be8186d74bc073d458bd85056.png",
    overlay: "rgba(0,0,0,0.1)",
    blur: "blur(0px)",
    accent: "#aaaaaa",
    glow: "rgba(180,180,180,0.3)",
    gradient: "radial-gradient(ellipse at center, rgba(20,20,20,0.35) 0%, rgba(5,5,5,0.75) 70%)"
  },
  monolith: {
    name: "Monolith",
    nameTranslations: {
      en: "Monolith",
      es: "Monolito",
      fr: "Monolithe",
      de: "Monolith",
      zh: "巨石",
      ja: "モノリス",
      pt: "Monólito",
      ar: "متراصة",
      ru: "Монолит",
      ko: "모노리스"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/623f944e65dde545c1fa45d7e146cdcd773ecc2021aa50506d4066d8a5b11912.png",
    overlay: "rgba(0,0,0,0.05)",
    blur: "blur(0px)",
    accent: "#cc66ff",
    glow: "rgba(200,100,255,0.4)",
    gradient: "radial-gradient(ellipse at center, rgba(20,10,30,0.3) 0%, rgba(10,5,15,0.7) 70%)"
  },
  apocalypse: {
    name: "Apocalypse",
    nameTranslations: {
      en: "Apocalypse",
      es: "Apocalipsis",
      fr: "Apocalypse",
      de: "Apokalypse",
      zh: "末日",
      ja: "黙示録",
      pt: "Apocalipse",
      ar: "نهاية العالم",
      ru: "Апокалипсис",
      ko: "종말"
    },
    image: "https://static.prod-images.emergentagent.com/jobs/3a9a6ba0-202e-4c8f-8ae0-b0ecce5f76d9/images/e22fe5b9eca3cee50a9f3aef79e8031e3c794bec98cfd9a4ad1f9332f07b6f61.png",
    overlay: "rgba(0,0,0,0.05)",
    blur: "blur(0px)",
    accent: "#ff6600",
    glow: "rgba(255,100,0,0.5)",
    gradient: "radial-gradient(ellipse at center, rgba(25,10,0,0.3) 0%, rgba(15,5,0,0.7) 70%)"
  }
};

// Combined themes for premium/owner mode
const getAllThemes = () => ({ ...publicThemes, ...ownerThemes });

// Get all theme keys for random selection
const allThemeKeys = [...Object.keys(publicThemes), ...Object.keys(ownerThemes)];
const publicThemeKeys = Object.keys(publicThemes);

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPayment, setShowPayment] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  
  // Check if user is owner (from localStorage - set by Dashboard when owner code is entered)
  const [isOwner, setIsOwner] = useState(() => {
    return localStorage.getItem('skybot_owner_mode') === 'true';
  });
  
  // Check if user has premium access
  const [isPremium, setIsPremium] = useState(() => {
    return localStorage.getItem('skybot_premium') === 'true';
  });
  
  // User has access to exclusive themes if they are owner OR premium
  const hasExclusiveAccess = isOwner || isPremium;
  
  // Get available themes based on access level
  const availableThemes = hasExclusiveAccess ? getAllThemes() : publicThemes;
  
  // Random theme selection for ALL users on each visit
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Pick random theme based on access level
    const themePool = (localStorage.getItem('skybot_owner_mode') === 'true' || 
                       localStorage.getItem('skybot_premium') === 'true') 
                       ? allThemeKeys : publicThemeKeys;
    const randomIndex = Math.floor(Math.random() * themePool.length);
    return themePool[randomIndex];
  });
  
  // Auto-rotate theme every 30 seconds for a dynamic experience
  useEffect(() => {
    const rotateTheme = () => {
      const themePool = hasExclusiveAccess ? allThemeKeys : publicThemeKeys;
      const currentIndex = themePool.indexOf(currentTheme);
      const nextIndex = (currentIndex + 1) % themePool.length;
      setCurrentTheme(themePool[nextIndex]);
    };
    
    const interval = setInterval(rotateTheme, 30000); // Change every 30 seconds
    return () => clearInterval(interval);
  }, [currentTheme, hasExclusiveAccess]);

  // Persist the active sky theme so the Dashboard can mirror the exact same
  // 8K backdrop + accent for a cohesive cross-page experience.
  useEffect(() => {
    localStorage.setItem('skybot_active_sky_theme', currentTheme);
  }, [currentTheme]);
  
  const { theme } = useTheme();
  const { language, setLanguage, t, languages } = useLanguage();
  
  // Random creepy sayings that rotate - MUST be after useLanguage()
  const creepySayings = {
    en: [
      "THEY ARE WATCHING",
      "THE TRUTH IS UP THERE",
      "WE ARE NOT ALONE",
      "SOMETHING IS COMING",
      "THE SKY HAS EYES",
      "THEY KNOW YOU'RE HERE",
      "LOOK UP. ALWAYS.",
      "THE VISITORS RETURN",
      "SILENCE BEFORE CONTACT",
      "SIGNALS DETECTED",
      "ANOMALY APPROACHING",
      "FIRST CONTACT IMMINENT"
    ],
    es: [
      "ELLOS ESTÁN OBSERVANDO",
      "LA VERDAD ESTÁ ALLÁ ARRIBA",
      "NO ESTAMOS SOLOS",
      "ALGO SE ACERCA",
      "EL CIELO TIENE OJOS",
      "SABEN QUE ESTÁS AQUÍ"
    ],
    fr: [
      "ILS NOUS OBSERVENT",
      "LA VÉRITÉ EST LÀ-HAUT",
      "NOUS NE SOMMES PAS SEULS",
      "QUELQUE CHOSE APPROCHE"
    ],
    de: [
      "SIE BEOBACHTEN UNS",
      "DIE WAHRHEIT IST DA OBEN",
      "WIR SIND NICHT ALLEIN",
      "ETWAS KOMMT"
    ],
    zh: ["他们正在观察", "真相在上面", "我们不是孤独的", "有什么东西来了"],
    ja: ["彼らは見ている", "真実は空にある", "私たちは一人ではない", "何かが来る"],
    pt: ["ELES ESTÃO OBSERVANDO", "A VERDADE ESTÁ LÁ EM CIMA", "NÃO ESTAMOS SOZINHOS"],
    ar: ["إنهم يراقبون", "الحقيقة في الأعلى", "لسنا وحدنا"],
    ru: ["ОНИ НАБЛЮДАЮТ", "ПРАВДА ТАМ НАВЕРХУ", "МЫ НЕ ОДИНОКИ"],
    ko: ["그들이 지켜보고 있다", "진실은 저 위에", "우리는 혼자가 아니다"]
  };
  
  const [currentSayingIndex, setCurrentSayingIndex] = useState(0);
  
  // Rotate creepy sayings every 5 seconds
  useEffect(() => {
    const sayings = creepySayings[language] || creepySayings.en;
    const interval = setInterval(() => {
      setCurrentSayingIndex(prev => (prev + 1) % sayings.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [language]);
  
  // Get current creepy saying
  const getCurrentSaying = () => {
    const sayings = creepySayings[language] || creepySayings.en;
    return sayings[currentSayingIndex % sayings.length];
  };

  useEffect(() => {
    trackPageView('/');
    // Check owner/premium status periodically (in case it changes in another tab)
    const checkAccess = () => {
      const ownerStatus = localStorage.getItem('skybot_owner_mode') === 'true';
      const premiumStatus = localStorage.getItem('skybot_premium') === 'true';
      if (ownerStatus !== isOwner) setIsOwner(ownerStatus);
      if (premiumStatus !== isPremium) setIsPremium(premiumStatus);
    };
    window.addEventListener('storage', checkAccess);
    return () => window.removeEventListener('storage', checkAccess);
  }, [isOwner, isPremium]);

  const activeTheme = availableThemes[currentTheme] || getAllThemes()[currentTheme] || publicThemes.crystal;

  // Get theme name in current language
  const getThemeName = (themeKey) => {
    const allThemes = getAllThemes();
    const themeData = allThemes[themeKey];
    if (!themeData) return themeKey;
    return themeData.nameTranslations[language] || themeData.name;
  };
  
  // Check if a theme is exclusive (owner/premium only)
  const isExclusiveTheme = (themeKey) => {
    return Object.keys(ownerThemes).includes(themeKey);
  };

  // Landing page translations
  const landingText = {
    en: {
      headline: "THEY ARE WATCHING",
      subheadline: "Are you watching back?",
      description: "Advanced AI-powered detection system to identify, track, and analyze unidentified aerial phenomena in real-time.",
      cta: "Begin Detection",
      stats: [
        { value: "X-Zoom", label: "Detection Range" },
        { value: "GPT-5.2", label: "AI Analysis" },
        { value: "8K", label: "Capture Quality" }
      ],
      features: [
        { title: "Signal Detection", desc: "Detect anomalous electromagnetic signatures" },
        { title: "Pattern Recognition", desc: "AI identifies non-human flight patterns" },
        { title: "Night Vision", desc: "Military-grade thermal imaging" },
        { title: "Real-time Tracking", desc: "Lock and follow unidentified objects" }
      ],
      premium: "Unlock Full Access",
      premiumDesc: "Access classified detection filters and unlimited analysis",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Choose Theme"
    },
    es: {
      headline: "ELLOS ESTÁN OBSERVANDO",
      subheadline: "¿Estás observando tú?",
      description: "Sistema de detección avanzado con IA para identificar, rastrear y analizar fenómenos aéreos no identificados en tiempo real.",
      cta: "Iniciar Detección",
      stats: [
        { value: "X-Zoom", label: "Rango de Detección" },
        { value: "GPT-5.2", label: "Análisis IA" },
        { value: "8K", label: "Calidad de Captura" }
      ],
      features: [
        { title: "Detección de Señales", desc: "Detecta firmas electromagnéticas anómalas" },
        { title: "Reconocimiento de Patrones", desc: "IA identifica patrones de vuelo no humanos" },
        { title: "Visión Nocturna", desc: "Imagen térmica de grado militar" },
        { title: "Rastreo en Tiempo Real", desc: "Bloquea y sigue objetos no identificados" }
      ],
      premium: "Desbloquear Acceso Total",
      premiumDesc: "Accede a filtros de detección clasificados y análisis ilimitado",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Elegir Tema"
    },
    fr: {
      headline: "ILS NOUS OBSERVENT",
      subheadline: "Les observez-vous?",
      description: "Système de détection avancé alimenté par IA pour identifier, suivre et analyser les phénomènes aériens non identifiés en temps réel.",
      cta: "Commencer la Détection",
      stats: [
        { value: "X-Zoom", label: "Portée de Détection" },
        { value: "GPT-5.2", label: "Analyse IA" },
        { value: "8K", label: "Qualité de Capture" }
      ],
      features: [
        { title: "Détection de Signal", desc: "Détecte les signatures électromagnétiques anormales" },
        { title: "Reconnaissance de Motifs", desc: "L'IA identifie les schémas de vol non humains" },
        { title: "Vision Nocturne", desc: "Imagerie thermique de grade militaire" },
        { title: "Suivi en Temps Réel", desc: "Verrouille et suit les objets non identifiés" }
      ],
      premium: "Débloquer l'Accès Complet",
      premiumDesc: "Accédez aux filtres de détection classifiés et à l'analyse illimitée",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Choisir le Thème"
    },
    de: {
      headline: "SIE BEOBACHTEN UNS",
      subheadline: "Beobachtest du zurück?",
      description: "Fortschrittliches KI-gestütztes Erkennungssystem zur Identifizierung, Verfolgung und Analyse unidentifizierter Luftphänomene in Echtzeit.",
      cta: "Erkennung Starten",
      stats: [
        { value: "X-Zoom", label: "Erkennungsbereich" },
        { value: "GPT-5.2", label: "KI-Analyse" },
        { value: "8K", label: "Aufnahmequalität" }
      ],
      features: [
        { title: "Signalerkennung", desc: "Erkennt anomale elektromagnetische Signaturen" },
        { title: "Mustererkennung", desc: "KI identifiziert nicht-menschliche Flugmuster" },
        { title: "Nachtsicht", desc: "Militärische Wärmebildgebung" },
        { title: "Echtzeit-Tracking", desc: "Verfolgt unidentifizierte Objekte" }
      ],
      premium: "Vollzugriff Freischalten",
      premiumDesc: "Zugang zu klassifizierten Erkennungsfiltern und unbegrenzter Analyse",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Thema Wählen"
    },
    zh: {
      headline: "他们正在观察",
      subheadline: "你在回望吗？",
      description: "先进的AI驱动检测系统，实时识别、跟踪和分析不明飞行现象。",
      cta: "开始检测",
      stats: [
        { value: "X-Zoom", label: "检测范围" },
        { value: "GPT-5.2", label: "AI分析" },
        { value: "8K", label: "捕获质量" }
      ],
      features: [
        { title: "信号检测", desc: "检测异常电磁信号" },
        { title: "模式识别", desc: "AI识别非人类飞行模式" },
        { title: "夜视", desc: "军用级热成像" },
        { title: "实时追踪", desc: "锁定并跟踪不明物体" }
      ],
      premium: "解锁完整访问",
      premiumDesc: "访问机密检测滤镜和无限分析",
      installAndroid: "安卓",
      installIOS: "苹果",
      chooseTheme: "选择主题"
    },
    ja: {
      headline: "彼らは見ている",
      subheadline: "あなたは見返していますか？",
      description: "未確認飛行現象をリアルタイムで識別、追跡、分析するAI搭載の高度な検出システム。",
      cta: "検出開始",
      stats: [
        { value: "X-Zoom", label: "検出範囲" },
        { value: "GPT-5.2", label: "AI分析" },
        { value: "8K", label: "キャプチャ品質" }
      ],
      features: [
        { title: "信号検出", desc: "異常な電磁波を検出" },
        { title: "パターン認識", desc: "AIが非人間的な飛行パターンを識別" },
        { title: "暗視", desc: "軍用グレードの熱画像" },
        { title: "リアルタイム追跡", desc: "未確認物体をロックして追跡" }
      ],
      premium: "フルアクセスを解除",
      premiumDesc: "機密検出フィルターと無制限の分析にアクセス",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "テーマを選択"
    },
    pt: {
      headline: "ELES ESTÃO OBSERVANDO",
      subheadline: "Você está observando de volta?",
      description: "Sistema de detecção avançado com IA para identificar, rastrear e analisar fenômenos aéreos não identificados em tempo real.",
      cta: "Iniciar Detecção",
      stats: [
        { value: "X-Zoom", label: "Alcance de Detecção" },
        { value: "GPT-5.2", label: "Análise IA" },
        { value: "8K", label: "Qualidade de Captura" }
      ],
      features: [
        { title: "Detecção de Sinal", desc: "Detecta assinaturas eletromagnéticas anômalas" },
        { title: "Reconhecimento de Padrões", desc: "IA identifica padrões de voo não humanos" },
        { title: "Visão Noturna", desc: "Imagem térmica de grau militar" },
        { title: "Rastreamento em Tempo Real", desc: "Trava e segue objetos não identificados" }
      ],
      premium: "Desbloquear Acesso Total",
      premiumDesc: "Acesse filtros de detecção classificados e análise ilimitada",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Escolher Tema"
    },
    ar: {
      headline: "إنهم يراقبون",
      subheadline: "هل تراقب أنت؟",
      description: "نظام كشف متقدم يعمل بالذكاء الاصطناعي لتحديد وتتبع وتحليل الظواهر الجوية غير المحددة في الوقت الفعلي.",
      cta: "بدء الكشف",
      stats: [
        { value: "X-Zoom", label: "نطاق الكشف" },
        { value: "GPT-5.2", label: "تحليل الذكاء" },
        { value: "8K", label: "جودة الالتقاط" }
      ],
      features: [
        { title: "كشف الإشارات", desc: "يكشف التوقيعات الكهرومغناطيسية الشاذة" },
        { title: "التعرف على الأنماط", desc: "الذكاء الاصطناعي يحدد أنماط الطيران غير البشرية" },
        { title: "الرؤية الليلية", desc: "تصوير حراري عسكري" },
        { title: "التتبع الفوري", desc: "قفل وتتبع الأجسام غير المحددة" }
      ],
      premium: "فتح الوصول الكامل",
      premiumDesc: "الوصول إلى مرشحات الكشف السرية والتحليل غير المحدود",
      installAndroid: "أندرويد",
      installIOS: "آيفون",
      chooseTheme: "اختر السمة"
    },
    ru: {
      headline: "ОНИ НАБЛЮДАЮТ",
      subheadline: "Вы наблюдаете в ответ?",
      description: "Продвинутая система обнаружения на базе ИИ для идентификации, отслеживания и анализа неопознанных воздушных явлений в реальном времени.",
      cta: "Начать Обнаружение",
      stats: [
        { value: "X-Zoom", label: "Дальность Обнаружения" },
        { value: "GPT-5.2", label: "ИИ Анализ" },
        { value: "8K", label: "Качество Захвата" }
      ],
      features: [
        { title: "Обнаружение Сигналов", desc: "Обнаруживает аномальные электромагнитные сигнатуры" },
        { title: "Распознавание Паттернов", desc: "ИИ определяет нечеловеческие схемы полета" },
        { title: "Ночное Видение", desc: "Тепловизор военного класса" },
        { title: "Отслеживание в Реальном Времени", desc: "Захват и слежение за неопознанными объектами" }
      ],
      premium: "Разблокировать Полный Доступ",
      premiumDesc: "Доступ к засекреченным фильтрам обнаружения и неограниченному анализу",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "Выбрать Тему"
    },
    ko: {
      headline: "그들이 지켜보고 있다",
      subheadline: "당신은 지켜보고 있나요?",
      description: "미확인 비행 현상을 실시간으로 식별, 추적 및 분석하는 AI 기반 고급 탐지 시스템.",
      cta: "탐지 시작",
      stats: [
        { value: "X-Zoom", label: "탐지 범위" },
        { value: "GPT-5.2", label: "AI 분석" },
        { value: "8K", label: "캡처 품질" }
      ],
      features: [
        { title: "신호 탐지", desc: "이상 전자기 신호 탐지" },
        { title: "패턴 인식", desc: "AI가 비인간 비행 패턴 식별" },
        { title: "야간 투시", desc: "군용 열화상" },
        { title: "실시간 추적", desc: "미확인 물체 잠금 및 추적" }
      ],
      premium: "전체 액세스 잠금 해제",
      premiumDesc: "기밀 탐지 필터 및 무제한 분석 액세스",
      installAndroid: "Android",
      installIOS: "iPhone",
      chooseTheme: "테마 선택"
    }
  };

  const text = landingText[language] || landingText.en;

  return (
    <div className="min-h-screen overflow-hidden relative" style={{ background: '#000' }}>
      {/* 8K Glass Background Image */}
      <div className="fixed inset-0 overflow-hidden">
        {/* Main 8K Image */}
        <motion.div
          key={currentTheme}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${activeTheme.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            filter: activeTheme.blur
          }}
        />
        
        {/* Glass Overlay Effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: activeTheme.gradient,
            backdropFilter: currentTheme === 'frosted' ? 'blur(12px) saturate(180%)' : 
                           currentTheme === 'holographic' ? 'blur(4px) saturate(150%) hue-rotate(5deg)' : 
                           'blur(0px) saturate(120%)'
          }}
        />
        
        {/* Color Overlay */}
        <div 
          className="absolute inset-0"
          style={{ background: activeTheme.overlay }}
        />
        
        {/* ===== UNIVERSAL ANIMATIONS FOR ALL THEMES ===== */}
        
        {/* Animated Light Beam Effect */}
        <motion.div
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 50% 30%, ${activeTheme.accent}15 0%, transparent 60%)`,
          }}
        />
        
        {/* Pulsing Glow Ring */}
        <motion.div
          animate={{
            scale: [0.8, 1.2, 0.8],
            opacity: [0.1, 0.25, 0.1],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${activeTheme.accent}30 0%, transparent 70%)`,
            filter: 'blur(40px)',
          }}
        />
        
        {/* Scanning Line Animation */}
        <motion.div
          animate={{
            y: ['-100%', '200%'],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute left-0 right-0 h-px pointer-events-none"
          style={{
            background: `linear-gradient(90deg, transparent, ${activeTheme.accent}60, transparent)`,
            boxShadow: `0 0 20px 5px ${activeTheme.accent}40`,
          }}
        />
        
        {/* Moving Stars Background */}
        {Array.from({ length: 30 }, (_, i) => ({
          left: (i * 13 + 7) % 100,
          top: (i * 17 + 3) % 100,
          size: 1 + (i % 3),
          duration: 2 + (i % 5),
          delay: i * 0.2
        })).map((star, i) => (
          <motion.div
            key={`star-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: star.size,
              height: star.size,
              backgroundColor: '#fff',
            }}
            animate={{
              opacity: [0.2, 1, 0.2],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: star.duration,
              repeat: Infinity,
              delay: star.delay,
            }}
          />
        ))}
        
        {/* Floating Light Orbs */}
        {Array.from({ length: 5 }, (_, i) => ({
          left: 10 + i * 20,
          duration: 8 + i * 2,
          delay: i * 1.5
        })).map((orb, i) => (
          <motion.div
            key={`orb-${i}`}
            className="absolute w-4 h-4 rounded-full pointer-events-none"
            style={{
              left: `${orb.left}%`,
              bottom: '10%',
              background: `radial-gradient(circle, ${activeTheme.accent}80, transparent)`,
              filter: 'blur(4px)',
            }}
            animate={{
              y: [0, -300, -500],
              x: [0, 30, -20, 40],
              opacity: [0, 0.8, 0],
              scale: [0.5, 1.5, 0.5],
            }}
            transition={{
              duration: orb.duration,
              repeat: Infinity,
              delay: orb.delay,
              ease: "easeInOut"
            }}
          />
        ))}
        
        {/* Corner Glow Effects */}
        <motion.div
          animate={{
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 left-0 w-1/3 h-1/3 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 0% 0%, ${activeTheme.accent}20 0%, transparent 70%)`,
          }}
        />
        <motion.div
          animate={{
            opacity: [0.1, 0.25, 0.1],
          }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-0 right-0 w-1/3 h-1/3 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 100% 100%, ${activeTheme.accent}20 0%, transparent 70%)`,
          }}
        />
        
        {/* Floating Particles Rising */}
        {Array.from({ length: 20 }, (_, i) => ({
          left: (i * 5 + 2) % 100,
          size: 1 + (i % 2),
          duration: 5 + (i % 4),
          delay: i * 0.3
        })).map((particle, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${particle.left}%`,
              bottom: '0%',
              width: particle.size,
              height: particle.size,
              backgroundColor: `${activeTheme.accent}`,
            }}
            animate={{
              y: [0, -800],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "linear"
            }}
          />
        ))}

        {/* Theme-specific Effects */}
        {(currentTheme === 'abduction' || currentTheme === 'invasion' || currentTheme === 'encounter') && (
          <motion.div
            animate={{
              opacity: [0.05, 0.15, 0.05],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 20%, ${activeTheme.accent}25 0%, transparent 50%)`,
            }}
          />
        )}
        
        {currentTheme === 'apocalypse' && (
          <>
            <motion.div
              animate={{
                opacity: [0.1, 0.3, 0.1],
                backgroundPosition: ['0% 0%', '100% 100%'],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at 70% 30%, rgba(255,100,0,0.2) 0%, transparent 40%)',
              }}
            />
          </>
        )}
      </div>

      {/* Sticky Header */}
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-[60] px-4 py-3"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${activeTheme.accent}30`
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Aperture className="w-7 h-7" style={{ color: activeTheme.accent }} />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full"
                style={{ border: `1px solid ${activeTheme.accent}40` }}
              />
            </div>
            <span className="font-bold text-white tracking-wider text-lg" style={{ textShadow: `0 0 20px ${activeTheme.accent}` }}>SKYBOT</span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Theme Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowThemes(!showThemes);
                  setShowLanguages(false);
                }}
                data-testid="theme-selector-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: `${activeTheme.accent}15`,
                  border: `1px solid ${activeTheme.accent}40`,
                  color: activeTheme.accent
                }}
              >
                <Palette className="w-4 h-4" />
                <span className="hidden sm:inline">{getThemeName(currentTheme)}</span>
                {isExclusiveTheme(currentTheme) && <Crown className="w-3 h-3 hidden sm:block" />}
                <ChevronDown className={`w-3 h-3 transition-transform ${showThemes ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Theme Dropdown */}
              <AnimatePresence>
                {showThemes && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute top-full mt-2 right-0 w-56 rounded-xl overflow-hidden z-50"
                    style={{
                      background: 'rgba(0,0,0,0.95)',
                      border: `1px solid ${activeTheme.accent}40`,
                      backdropFilter: 'blur(20px)',
                      boxShadow: `0 20px 40px rgba(0,0,0,0.5), 0 0 30px ${activeTheme.glow}`
                    }}
                  >
                    <div className="p-2 border-b border-white/10">
                      <span className="text-xs text-white/50 uppercase tracking-wider px-2">{text.chooseTheme}</span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {/* Public Themes */}
                      {Object.keys(publicThemes).map((themeKey) => {
                        const themeData = publicThemes[themeKey];
                        return (
                          <button
                            key={themeKey}
                            data-testid={`theme-option-${themeKey}`}
                            onClick={() => {
                              setCurrentTheme(themeKey);
                              setShowThemes(false);
                              toast.success(`Theme: ${getThemeName(themeKey)}`, {
                                icon: '✨'
                              });
                            }}
                            className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-all ${
                              currentTheme === themeKey 
                                ? 'text-white' 
                                : 'text-white/60 hover:text-white hover:bg-white/5'
                            }`}
                            style={{
                              background: currentTheme === themeKey ? `${themeData.accent}20` : 'transparent'
                            }}
                          >
                            {/* Theme Preview Circle */}
                            <div 
                              className="w-8 h-8 rounded-full overflow-hidden border-2 flex-shrink-0"
                              style={{
                                borderColor: currentTheme === themeKey ? themeData.accent : 'rgba(255,255,255,0.2)',
                                boxShadow: currentTheme === themeKey ? `0 0 12px ${themeData.glow}` : 'none'
                              }}
                            >
                              <img 
                                src={themeData.image} 
                                alt={getThemeName(themeKey)}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium">{getThemeName(themeKey)}</span>
                              <span className="text-xs opacity-50">8K Quality</span>
                            </div>
                            {currentTheme === themeKey && (
                              <Sparkles className="w-4 h-4 ml-auto" style={{ color: themeData.accent }} />
                            )}
                          </button>
                        );
                      })}
                      
                      {/* Premium/Owner Exclusive Themes Section */}
                      {hasExclusiveAccess && (
                        <>
                          <div className="p-2 border-t border-b border-purple-500/30 mt-2 bg-purple-500/10">
                            <div className="flex items-center gap-2 px-2">
                              <Crown className="w-3 h-3 text-purple-400" />
                              <span className="text-xs text-purple-400 uppercase tracking-wider">
                                {isOwner ? 'Owner' : 'Premium'} Themes
                              </span>
                            </div>
                          </div>
                          {Object.keys(ownerThemes).map((themeKey) => {
                            const themeData = ownerThemes[themeKey];
                            return (
                              <button
                                key={themeKey}
                                data-testid={`theme-option-${themeKey}`}
                                onClick={() => {
                                  setCurrentTheme(themeKey);
                                  setShowThemes(false);
                                  toast.success(`Theme: ${getThemeName(themeKey)}`, {
                                    icon: '👽'
                                  });
                                }}
                                className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-all ${
                                  currentTheme === themeKey 
                                    ? 'text-white' 
                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                                }`}
                                style={{
                                  background: currentTheme === themeKey ? `${themeData.accent}20` : 'transparent'
                                }}
                              >
                                {/* Theme Preview Circle */}
                                <div 
                                  className="w-8 h-8 rounded-full overflow-hidden border-2 flex-shrink-0"
                                  style={{
                                    borderColor: currentTheme === themeKey ? themeData.accent : 'rgba(255,255,255,0.2)',
                                    boxShadow: currentTheme === themeKey ? `0 0 12px ${themeData.glow}` : 'none'
                                  }}
                                >
                                  <img 
                                    src={themeData.image} 
                                    alt={getThemeName(themeKey)}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-medium">{getThemeName(themeKey)}</span>
                                  <span className="text-xs opacity-50">8K Exclusive</span>
                                </div>
                                {currentTheme === themeKey && (
                                  <Sparkles className="w-4 h-4 ml-auto" style={{ color: themeData.accent }} />
                                )}
                              </button>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Locked themes indicator for non-premium/non-owner users */}
                      {!hasExclusiveAccess && (
                        <div className="p-3 border-t border-white/10 mt-2">
                          <div className="flex items-center gap-2 text-white/30 text-xs">
                            <Lock className="w-3 h-3" />
                            <span>5 exclusive themes • Premium or Owner</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowLanguages(!showLanguages);
                  setShowThemes(false);
                }}
                data-testid="language-selector-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: `${activeTheme.accent}15`,
                  border: `1px solid ${activeTheme.accent}40`,
                  color: activeTheme.accent
                }}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{languageNames[language]?.flag} {languageNames[language]?.name}</span>
                <span className="sm:hidden">{languageNames[language]?.flag}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showLanguages ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Language Dropdown */}
              <AnimatePresence>
                {showLanguages && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute top-full mt-2 right-0 w-48 rounded-xl overflow-hidden z-50 max-h-80 overflow-y-auto"
                    style={{
                      background: 'rgba(0,0,0,0.95)',
                      border: `1px solid ${activeTheme.accent}40`,
                      backdropFilter: 'blur(20px)',
                      boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                    }}
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang}
                        data-testid={`language-option-${lang}`}
                        onClick={() => {
                          setLanguage(lang);
                          setShowLanguages(false);
                          toast.success(`Language: ${languageNames[lang]?.name}`);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                          language === lang 
                            ? 'text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        style={{
                          background: language === lang ? `${activeTheme.accent}20` : 'transparent'
                        }}
                      >
                        <span className="text-lg">{languageNames[lang]?.flag}</span>
                        <span>{languageNames[lang]?.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Premium Button */}
            <motion.button
              data-testid="sticky-premium-btn"
              onClick={() => setShowPayment(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm"
              style={{
                background: `linear-gradient(135deg, ${activeTheme.accent}, ${activeTheme.accent}cc)`,
                color: '#000',
                boxShadow: `0 4px 20px ${activeTheme.glow}`
              }}
            >
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">{t('premium')}</span>
              <span className="text-xs opacity-80">$9.99</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Stripe Payment Modal */}
      <StripePayment 
        isOpen={showPayment} 
        onClose={() => setShowPayment(false)}
        onPaymentComplete={() => {
          setShowPayment(false);
          navigate('/dashboard');
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center px-6 pt-24 pb-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Signal indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-2 mb-6"
          >
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: activeTheme.accent }}
            />
            <span className="text-xs tracking-[0.3em] uppercase" style={{ color: `${activeTheme.accent}90` }}>
              Signal Detected
            </span>
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: activeTheme.accent }}
            />
          </motion.div>

          {/* Main Headline */}
          {/* Main Headline - Random Creepy Sayings */}
          <motion.h1
            key={getCurrentSaying()}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-black mb-4 tracking-tight"
            style={{
              color: activeTheme.accent,
              textShadow: `0 0 60px ${activeTheme.glow}, 0 0 120px ${activeTheme.glow}`
            }}
          >
            {getCurrentSaying()}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl sm:text-2xl text-white/60 mb-6 font-light"
          >
            {text.subheadline}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/40 text-sm sm:text-base max-w-2xl mx-auto mb-10"
          >
            {text.description}
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center gap-8 sm:gap-16 mb-12"
          >
            {text.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: activeTheme.accent }}>{stat.value}</p>
                <p className="text-xs text-white/40 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <motion.button
              data-testid="begin-detection-btn"
              onClick={() => navigate('/dashboard')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-3 group"
              style={{
                background: `linear-gradient(135deg, ${activeTheme.accent}, ${activeTheme.accent}cc)`,
                color: '#000',
                boxShadow: `0 8px 40px ${activeTheme.glow}`
              }}
            >
              <Scan className="w-5 h-5" />
              {text.cta}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <div className="flex gap-3">
              <DownloadAppButton variant="default" />
              
              {/* iPhone Install */}
              <motion.button
                data-testid="iphone-install-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  toast.info(
                    <div className="text-left">
                      <p className="font-bold mb-2">📱 {text.installIOS}:</p>
                      <p>1. Tap Share ⬆️</p>
                      <p>2. &quot;Add to Home Screen&quot;</p>
                    </div>,
                    { duration: 6000 }
                  );
                }}
                className="px-4 py-3 rounded-xl font-bold text-sm flex items-center gap-2"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff'
                }}
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.08-.46-2.07-.48-3.2 0-1.42.62-2.16.44-3.02-.35C3.38 15.86 4.05 9.63 8.9 9.37c1.28.06 2.17.7 2.92.73.94-.19 1.84-.79 2.88-.74 1.22.08 2.15.58 2.75 1.48-2.53 1.51-1.93 4.84.55 5.77-.46 1.21-1.05 2.4-1.95 3.67zM12.3 9.3C12.08 7.09 13.86 5.28 16 5c.32 2.4-2.16 4.2-3.7 4.3z"/>
                </svg>
                {text.installIOS}
              </motion.button>
            </div>
          </motion.div>

          {/* Feature Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
          >
            {text.features.map((feature, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.02 }}
                className="p-4 rounded-xl text-left transition-all"
                style={{
                  background: `${activeTheme.accent}08`,
                  border: `1px solid ${activeTheme.accent}25`,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {i === 0 && <Radio className="w-4 h-4" style={{ color: activeTheme.accent }} />}
                  {i === 1 && <Radar className="w-4 h-4" style={{ color: activeTheme.accent }} />}
                  {i === 2 && <Eye className="w-4 h-4" style={{ color: activeTheme.accent }} />}
                  {i === 3 && <Wifi className="w-4 h-4" style={{ color: activeTheme.accent }} />}
                  <span className="text-white font-bold text-sm">{feature.title}</span>
                </div>
                <p className="text-white/40 text-xs">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Premium Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="max-w-md mx-auto p-6 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${activeTheme.accent}15, ${activeTheme.accent}05)`,
              border: `1px solid ${activeTheme.accent}40`,
              backdropFilter: 'blur(20px)'
            }}
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Crown className="w-5 h-5" style={{ color: activeTheme.accent }} />
              <span className="font-bold" style={{ color: activeTheme.accent }}>{text.premium}</span>
            </div>
            <p className="text-white/50 text-sm mb-4">{text.premiumDesc}</p>
            <motion.button
              data-testid="premium-unlock-btn"
              onClick={() => setShowPayment(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 rounded-xl font-bold"
              style={{
                background: `linear-gradient(135deg, ${activeTheme.accent}, ${activeTheme.accent}cc)`,
                color: '#000'
              }}
            >
              $9.99 / {t('premium')}
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* PWA Install Banner */}
      <FloatingInstallBanner />
    </div>
  );
};

export default LandingPage;
